import { APILoadingStatus } from '@vis.gl/react-google-maps';

/**
 * How long a caller waits for the Google Maps JS SDK to finish loading before
 * treating the load as failed and surfacing an error.
 *
 * The SDK script itself has no built-in timeout: `<APIProvider>` mounts and
 * injects the script asynchronously, and a dead network at the moment of
 * injection would otherwise leave a caller waiting on `window.google` forever
 * with no FAILED/AUTH_FAILURE status to react to. This bounds that wait.
 */
export const MAPS_LOAD_TIMEOUT_MS = 15_000;

/** How often {@link waitForGoogleMapsReady} re-checks readiness while waiting. */
const POLL_INTERVAL_MS = 200;

export type MapsReadyOutcome = 'READY' | 'OFFLINE' | 'FAILED' | 'AUTH_FAILURE' | 'TIMEOUT';

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Waits for the Maps JS SDK to become usable (`window.google.maps.Geocoder`
 * exists) instead of racing it. Reads live status through `getStatus` on each
 * poll rather than once, so the wait actually reacts to a status transition
 * fired by `<APIProvider>` after this function starts (`getStatus` is
 * typically a ref-backed getter kept in sync with `useApiLoadingStatus()`,
 * since the status itself can only be read from a React hook).
 *
 * Resolves as soon as any of:
 * - `READY` — the Geocoder is available.
 * - `OFFLINE` — the device has no network (checked first and on every poll —
 *   there is nothing productive to wait for, and this is what the user should
 *   be told instead of a generic failure).
 * - `FAILED` / `AUTH_FAILURE` — `<APIProvider>`'s own loader reported the SDK
 *   load failed (bad/over-quota key, blocked script, auth rejection).
 * - `TIMEOUT` — none of the above happened within `timeoutMs`.
 *
 * Never rejects — every branch is a fault a caller can render, not an
 * exception to unwind.
 *
 * Accepts an optional `signal` so a caller that goes away mid-wait (a
 * component unmounts, an address changes) can cancel cleanly: aborting
 * clears the pending poll timer — so nothing is left scheduled — and the
 * returned promise is left permanently unsettled. That is "resolve/reject
 * quietly": every caller only acts inside a `.then()`/`await` continuation,
 * so a promise that never settles after abort means that continuation
 * simply never runs — no toast, no logError, for a screen nobody is on
 * anymore.
 */
export function waitForGoogleMapsReady(
  getStatus: () => APILoadingStatus,
  timeoutMs: number = MAPS_LOAD_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<MapsReadyOutcome> {
  if (window.google?.maps?.Geocoder) return Promise.resolve('READY');
  if (isOffline()) return Promise.resolve('OFFLINE');
  // Already aborted before the first check — never schedule anything.
  if (signal?.aborted) return new Promise<MapsReadyOutcome>(() => {});

  const startedAt = Date.now();

  return new Promise<MapsReadyOutcome>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const onAbort = () => {
      if (timer !== undefined) clearTimeout(timer);
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    const settle = (outcome: MapsReadyOutcome) => {
      signal?.removeEventListener('abort', onAbort);
      resolve(outcome);
    };

    const check = () => {
      if (signal?.aborted) return;
      if (window.google?.maps?.Geocoder) {
        settle('READY');
        return;
      }
      if (isOffline()) {
        settle('OFFLINE');
        return;
      }
      const status = getStatus();
      if (status === APILoadingStatus.FAILED) {
        settle('FAILED');
        return;
      }
      if (status === APILoadingStatus.AUTH_FAILURE) {
        settle('AUTH_FAILURE');
        return;
      }
      if (Date.now() - startedAt >= timeoutMs) {
        settle('TIMEOUT');
        return;
      }
      timer = setTimeout(check, POLL_INTERVAL_MS);
    };
    check();
  });
}

/**
 * A user-facing message for every non-READY outcome. Says what to do, not
 * just that something broke — the map/geocode panel already had a grey
 * "loading" state for the in-progress case; this is only shown once we have
 * given up waiting.
 */
export function describeMapsUnavailable(outcome: Exclude<MapsReadyOutcome, 'READY'>): string {
  switch (outcome) {
    case 'OFFLINE':
      return "You're offline. Reconnect and try again.";
    case 'AUTH_FAILURE':
      return 'Map unavailable right now (Google Maps rejected our API key). Use Open in Google Maps.';
    case 'FAILED':
    case 'TIMEOUT':
      return 'Map unavailable right now. Use Open in Google Maps.';
    default: {
      const _exhaustive: never = outcome;
      return _exhaustive;
    }
  }
}
